import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
} from '@backstage/theme';

/* ─────────────────────────────────────────────────────
   A-Vertex / Ascentt Design System — synced with Figma
   Figma file: "Ant Design Open Source Ascentt"
   ───────────────────────────────────────────────────── */

/* Design tokens from Figma */
const tokens = {
  /* Brand */
  primary: '#F58321',
  primaryDark: '#E5751F',
  primaryLight: '#FFA366',

  /* Neutrals */
  neutral1: '#FFFFFF',
  neutral4: '#F0F0F0',
  neutral5: '#D9D9D9',
  neutral7: '#8C8C8C',
  neutral11: '#1F1F1F',
  neutral12: '#141414',

  /* Status / Accent */
  blue6: '#1890FF',
  green6: '#52C41A',
  green7: '#389E0D',
  red6: '#F5222D',
  red7: '#CF1322',
  yellow6: '#FADB14',
  purple5: '#9254DE',

  /* Text on light background */
  textPrimary: 'rgba(0, 0, 0, 0.85)',
  textSecondary: 'rgba(0, 0, 0, 0.45)',
  textDisabled: 'rgba(0, 0, 0, 0.25)',

  /* Surfaces */
  bgGradient: 'linear-gradient(118deg, #EEEEEE 5.1%, #FFF1E7 93%)',

  /* Borders */
  border: '#EFEFEF',
  divider: '#F0F0F0',

  /* Font family */
  fontFamily: '"Work Sans", sans-serif',

  /* Radii */
  radiusCard: '24px',
  radiusPill: '9999px',
  radiusSmall: '8px',
};

export const myTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    fontFamily: tokens.fontFamily,
    palette: {
      ...palettes.light,
      primary: {
        main: tokens.primary,
        dark: tokens.primaryDark,
        light: tokens.primaryLight,
      },
      secondary: {
        main: tokens.neutral7,
      },
      banner: {
        info: tokens.primary,
        error: tokens.red6,
        text: tokens.neutral12,
        link: tokens.blue6,
      },
      errorBackground: tokens.red6,
      warningBackground: tokens.yellow6,
      infoBackground: tokens.blue6,
      navigation: {
        background: 'transparent',
        indicator: tokens.primary,
        color: tokens.neutral12,
        selectedColor: tokens.primary,
      },
      typography: {
        fontFamily: tokens.fontFamily,
        h1: {
          fontFamily: tokens.fontFamily,
          fontSize: '3.5rem',
          fontWeight: 400,
          lineHeight: 1.18,
        },
        h2: {
          fontFamily: tokens.fontFamily,
          fontSize: '1.875rem', /* 30px — H2/medium in Figma */
          fontWeight: 500,
          lineHeight: 1.33,
        },
        h3: {
          fontFamily: tokens.fontFamily,
          fontSize: '1.5rem', /* 24px — H3/medium */
          fontWeight: 500,
          lineHeight: 1.33,
        },
        h4: {
          fontFamily: tokens.fontFamily,
          fontSize: '1.25rem', /* 20px — H4/medium */
          fontWeight: 500,
          lineHeight: 1.4,
        },
        h5: {
          fontFamily: tokens.fontFamily,
          fontSize: '1rem', /* 16px — H5/medium */
          fontWeight: 500,
          lineHeight: 1.5,
        },
        h6: {
          fontFamily: tokens.fontFamily,
          fontSize: '0.875rem', /* 14px */
          fontWeight: 500,
          lineHeight: 1.43,
        },
        body1: {
          fontFamily: tokens.fontFamily,
          fontSize: '0.875rem', /* 14px — Body/regular */
          fontWeight: 400,
          lineHeight: 1.43,
        },
        body2: {
          fontFamily: tokens.fontFamily,
          fontSize: '0.75rem', /* 12px — Footnote/description */
          fontWeight: 400,
          lineHeight: 1.67,
        },
        button: {
          fontFamily: tokens.fontFamily,
          fontWeight: 500,
          textTransform: 'none' as const,
        },
      },
    },
  }),
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: [tokens.primary, tokens.primaryDark], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    catalog: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    documentation: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    tool: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    service: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    website: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    library: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    other: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    app: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
    apis: genPageTheme({ colors: [tokens.primary, tokens.neutral7], shape: 'none', options: { fontColor: tokens.neutral12 } }),
  },

  components: {
    /* ─── Backstage shell ─── */
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          width: '0px !important',
          display: 'none',
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          overflowX: 'hidden',
          paddingLeft: '0 !important',
          background: tokens.bgGradient,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column' as const,
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          overflowX: 'hidden',
          backgroundColor: 'transparent',
          borderTopLeftRadius: '0',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column' as const,
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          flex: 1,
          minHeight: 0,
          overflowY: 'visible',
          overflowX: 'visible',
          padding: 0,
        },
      },
    },

    /* ─── Page header → transparent, text-only (no filled bar) ─── */
    BackstageHeader: {
      styleOverrides: {
        header: {
          background: 'transparent !important',
          boxShadow: 'none',
          padding: '12px 24px 4px',
          minHeight: 'unset',
          borderBottom: 'none',
          borderRadius: 0,
          margin: 0,
        },
        title: {
          color: tokens.neutral12,
          fontFamily: tokens.fontFamily,
          fontSize: '1.125rem',
          fontWeight: 600,
          marginBottom: 0,
          lineHeight: 1.4,
        },
        subtitle: {
          color: tokens.textSecondary,
          fontFamily: tokens.fontFamily,
          fontSize: '0.75rem',
          lineHeight: 1.3,
          marginTop: '2px',
          opacity: 0.8,
          maxWidth: '150ch',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        },
        breadcrumb: {
          color: tokens.textSecondary,
          fontSize: '0.6875rem',
        },
        breadcrumbType: {
          color: tokens.textSecondary,
          fontSize: '0.6875rem',
        },
        breadcrumbTitle: {
          color: tokens.textPrimary,
          fontSize: '0.6875rem',
        },
      },
    },

    /* ─── Header tabs → orange underline (matches project-estimator) ─── */
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: 'transparent',
          padding: '0 24px',
          minHeight: '36px',
          borderBottom: '1px solid #E5E5E5',
          marginBottom: '0',
        },
        defaultTab: {
          textTransform: 'none' as const,
          fontSize: '0.8125rem',
          fontWeight: 500,
          fontFamily: tokens.fontFamily,
          color: tokens.textSecondary,
          letterSpacing: 0,
          padding: '8px 16px',
          minHeight: '36px',
          borderRadius: 0,
          margin: 0,
          transition: 'color 0.15s ease',
          '&:hover': {
            color: tokens.textPrimary,
          },
        },
        selected: {
          color: `${tokens.primary} !important`,
          fontWeight: 500,
        },
        tabRoot: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },

    /* ─── MUI Tabs — orange indicator (matches project-estimator) ─── */
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '36px',
        },
        indicator: {
          backgroundColor: tokens.primary,
          height: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '36px',
          minWidth: 'unset !important',
          textTransform: 'none' as const,
          fontFamily: tokens.fontFamily,
          fontWeight: 500,
          '&.Mui-selected': {
            color: `${tokens.primary} !important`,
          },
        },
      },
    },

    /* ─── MUI overrides to match Figma ─── */
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDark} 100%)`,
          borderRadius: tokens.radiusPill,
          boxShadow: 'none',
          textTransform: 'none' as const,
          fontWeight: 500,
          '&:hover': {
            background: `linear-gradient(135deg, ${tokens.primaryDark} 0%, #D4640E 100%)`,
            boxShadow: '0 4px 12px rgba(245, 131, 33, 0.25)',
          },
        },
        outlinedPrimary: {
          borderRadius: tokens.radiusPill,
          borderColor: tokens.primary,
          textTransform: 'none' as const,
          fontWeight: 500,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          zIndex: '5 !important' as any,
          position: 'relative' as const,
          maxHeight: 'calc(100vh - 140px)',
          marginTop: 0,
          top: 0,
        },
      },
    },

    /* ─── Scaffolder stepper → simplified, clean UI ─── */
    MuiStepper: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          padding: '16px 0',
          borderBottom: `1px solid ${tokens.divider}`,
          marginBottom: '24px',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontFamily: tokens.fontFamily,
          fontSize: '0.8125rem',
          fontWeight: 400,
          color: tokens.textSecondary,
          '&.Mui-active': {
            fontWeight: 500,
            color: tokens.textPrimary,
          },
          '&.Mui-completed': {
            fontWeight: 400,
            color: tokens.green7,
          },
        },
        iconContainer: {
          '& .MuiStepIcon-root': {
            width: '24px',
            height: '24px',
            color: tokens.neutral5,
            '&.Mui-active': {
              color: tokens.primary,
            },
            '&.Mui-completed': {
              color: tokens.green6,
            },
          },
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: tokens.neutral5,
          borderTopWidth: '2px',
        },
        active: {
          '& .MuiStepConnector-line': {
            borderColor: tokens.primary,
          },
        },
        completed: {
          '& .MuiStepConnector-line': {
            borderColor: tokens.green6,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: tokens.radiusCard,
        },
        outlined: {
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radiusCard,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusCard,
          border: `1px solid ${tokens.border}`,
          boxShadow: 'none',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '28px',
          borderRadius: tokens.radiusSmall,
          backgroundColor: 'rgba(245, 131, 33, 0.06)',
          border: `1px solid rgba(245, 131, 33, 0.15)`,
          fontSize: '0.75rem',
          fontWeight: 400,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          marginBottom: '8px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: tokens.textSecondary,
        },
      },
    },
    /* ─── Tables → Figma "Ant Design Open Source Ascentt" ─── */
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: '1px solid #E9EAEB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(10, 13, 18, 0.05)',
          overflow: 'hidden',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& th': {
            backgroundColor: '#FAFAFA',
            borderBottom: '1px solid #E9EAEB',
            color: '#717680',
            fontFamily: tokens.fontFamily,
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: '18px',
            letterSpacing: '0',
            textTransform: 'none' as const,
            padding: '12px 24px',
            height: '44px',
            whiteSpace: 'nowrap' as const,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& tr:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: '#FAFAFA !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E9EAEB',
          fontFamily: tokens.fontFamily,
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: '20px',
          color: '#181D27',
          padding: '16px 24px',
        },
        sizeSmall: {
          padding: '8px 16px',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: '#717680 !important',
          '&:hover': {
            color: '#414651 !important',
          },
          '&.Mui-active': {
            color: '#181D27 !important',
          },
        },
        icon: {
          opacity: '0.5 !important',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #E9EAEB',
          fontFamily: tokens.fontFamily,
          fontSize: '0.875rem',
          color: '#535862',
        },
        selectLabel: {
          fontFamily: tokens.fontFamily,
          fontSize: '0.875rem',
          color: '#535862',
        },
        displayedRows: {
          fontFamily: tokens.fontFamily,
          fontSize: '0.875rem',
          color: '#535862',
        },
        actions: {
          '& .MuiIconButton-root': {
            border: '1px solid #D5D7DA',
            borderRadius: '8px',
            padding: '6px',
            margin: '0 4px',
            boxShadow: '0 1px 2px rgba(10, 13, 18, 0.05)',
            '&:hover': {
              backgroundColor: '#FAFAFA',
            },
            '&.Mui-disabled': {
              opacity: 0.4,
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          fontFamily: tokens.fontFamily,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.divider,
        },
      },
    },
  },
});
